import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Search, Trash2, UserPlus, Car, Edit, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";

// User type definition
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "student" | "instructor" | "admin";
  createdAt: string;
  status: "active" | "inactive";
  phone?: string;
  address?: string;
  postcode?: string;
  gearbox?: string;
  licenseNumber?: string;
  experience?: string;
  carModel?: string;
  specialties?: string;
  availability?: string;
  displayName?: string;
  updatedAt?: string;
}

const UsersManagement = () => {
  const { toast } = useToast();
  const { createUser, getAllUsers, deleteUser } = useFirebase();
  const [searchTerm, setSearchTerm] = useState("");
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "student" as "student" | "instructor" | "admin",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Load users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const firestoreUsers = await getAllUsers();
        setUsers(firestoreUsers as User[]);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast({
          title: "Error",
          description: "Failed to load users",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.firstName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleCreateUser = async () => {
    // Validate form
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create user in Firestore
      const userData = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        password: newUser.password, // Note: In production, don't store plain passwords
        role: newUser.role,
        createdAt: new Date().toISOString(),
        status: "active",
        displayName: `${newUser.firstName} ${newUser.lastName}`,
        phone: "",
        address: "",
        postcode: "",
        gearbox: "",
      };

      const userId = await createUser(userData);
      
      toast({
        title: "Success",
        description: `User ${newUser.firstName} ${newUser.lastName} created successfully`,
      });
      
      // Refresh user list
      const updatedUsers = await getAllUsers();
      setUsers(updatedUsers as User[]);
      
      // Reset form and close dialog
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "student",
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error creating user:", error);
      toast({
        title: "Error",
        description: "Failed to create user. Email may already be in use.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      try {
        // Delete user from Firestore
        await deleteUser(userId);
        
        // Update local state
        setUsers(users.filter(user => user.id !== userId));
        
        toast({
          title: "Success",
          description: "User has been deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting user:", error);
        toast({
          title: "Error",
          description: "Failed to delete user. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add New User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={newUser.firstName}
                      onChange={handleInputChange}
                      placeholder="John"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={newUser.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    placeholder="john.doe@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={newUser.password}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <select
                    id="role"
                    name="role"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    value={newUser.role}
                    onChange={handleInputChange}
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateUser}>Create User</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        <p className="text-gray-600">
          Manage student and instructor accounts, assign instructors to students, and track user activity.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Users</CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          user.role === "admin"
                            ? "bg-purple-500"
                            : user.role === "instructor"
                            ? "bg-blue-500"
                            : "bg-green-500"
                        }
                      >
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          user.status === "active"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }
                      >
                        {user.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedUser(user);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Basic Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {`${selectedUser.firstName} ${selectedUser.lastName}`}</p>
                    <p><span className="font-medium">Email:</span> {selectedUser.email}</p>
                    <p><span className="font-medium">Role:</span> {selectedUser.role}</p>
                    <p><span className="font-medium">Status:</span> {selectedUser.status}</p>
                    <p><span className="font-medium">Created:</span> {new Date(selectedUser.createdAt).toLocaleString()}</p>
                    {selectedUser.updatedAt && (
                      <p><span className="font-medium">Last Updated:</span> {new Date(selectedUser.updatedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Contact Information</h3>
                  <div className="space-y-2">
                    {selectedUser.phone && <p><span className="font-medium">Phone:</span> {selectedUser.phone}</p>}
                    {selectedUser.address && <p><span className="font-medium">Address:</span> {selectedUser.address}</p>}
                    {selectedUser.postcode && <p><span className="font-medium">Postcode:</span> {selectedUser.postcode}</p>}
                  </div>
                </div>
              </div>

              {selectedUser.role === "student" && (
                <div>
                  <h3 className="font-semibold mb-2">Student Information</h3>
                  <div className="space-y-2">
                    {selectedUser.gearbox && <p><span className="font-medium">Preferred Gearbox:</span> {selectedUser.gearbox}</p>}
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Learning Progress</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Completed Lessons</p>
                          <p className="text-lg font-semibold">0</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">Upcoming Lessons</p>
                          <p className="text-lg font-semibold">0</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Course Information</h4>
                      <div className="space-y-2">
                        <p><span className="font-medium">Current Course:</span> Not enrolled</p>
                        <p><span className="font-medium">Start Date:</span> -</p>
                        <p><span className="font-medium">Expected Completion:</span> -</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Payment Status</h4>
                      <div className="space-y-2">
                        <p><span className="font-medium">Last Payment:</span> -</p>
                        <p><span className="font-medium">Payment Status:</span> <Badge variant="outline">No payments</Badge></p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedUser.role === "instructor" && (
                <div>
                  <h3 className="font-semibold mb-2">Instructor Information</h3>
                  <div className="space-y-2">
                    {selectedUser.licenseNumber && <p><span className="font-medium">License Number:</span> {selectedUser.licenseNumber}</p>}
                    {selectedUser.experience && <p><span className="font-medium">Years of Experience:</span> {selectedUser.experience}</p>}
                    {selectedUser.carModel && <p><span className="font-medium">Car Model:</span> {selectedUser.carModel}</p>}
                    {selectedUser.specialties && <p><span className="font-medium">Specialties:</span> {selectedUser.specialties}</p>}
                    {selectedUser.availability && <p><span className="font-medium">Availability:</span> {selectedUser.availability}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => {
                setIsViewDialogOpen(false);
                handleDeleteUser(selectedUser.id);
              }}
            >
              Delete User
            </Button>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default UsersManagement;
